import { FC } from 'react'

import { Input } from '../../../components/Input/Input'
import { POST } from '../../../services/api'
import { i18n } from '../../../translate/i18n'

export type CustomerFormData = {
  // Dados pessoais básicos
  name: string
  email: string
  cpf: string
  birthDate: string
  gender: 'masculino' | 'feminino' | 'outro' | ''

  // Contato
  phoneNumber: string
  phoneType: 'cell' | 'landline'
  optinWhatsapp: boolean

  // Endereço
  zipCode: string
  street: string
  number: string
  extra?: string
  district: string
  city: string
  state: string
  reference?: string

  // Termos e condições
  acceptTerms: boolean
  lgpdConsent: boolean
}

export interface CustomerRegistrationData {
  id?: number
  pluginconfigId?: number
  name: string
  validateRegister?: boolean
  email: string
  doc: string
  password?: string
  addresses: AddressDataCustomer[]
  phones: DataPhoneCustomer[]
  hash?: string
  type?: string
  cpf: string
  optinWhatsapp: boolean
  birthDate: string
  gender: string
  lgpdDataAceiteCarta?: Date
  inscEstadual?: string
  customerCode?: number
  idTerms?: number
  status?: 'active' | 'inactive' | 'blocked'
  created?: Date
  updated?: Date
  deleted?: Date
  success?: boolean
  message?: string
}

export interface AddressDataCustomer {
  id?: number
  customerId?: number
  zipCode: string
  street: string
  number: string
  extra?: string
  district: string
  city: string
  state: string
  reference?: string
  typeAddress: 'delivery' | 'payment' | 'main'
  latlng?: {
    lat: number
    lng: number
  }
  placeId?: string
  ibge?: string
  ibgeCode?: string
  created?: Date
  updated?: Date
}

export interface DataPhoneCustomer {
  customerId?: number
  typePhone: 'principal' | 'secondary' | 'message' | 'cell' | 'landline'
  numberPhone: string
}

type Props = {
  customerData: CustomerFormData
  onCustomerChange: (data: CustomerFormData) => void
  disabled?: boolean
  showTitle?: boolean
}

const CustomerForm: FC<Props> = ({
  customerData,
  onCustomerChange,
  disabled = false,
  showTitle = true
}) => {

  const handleInputChange = (field: keyof CustomerFormData, value: string | boolean) => {
    onCustomerChange({
      ...customerData,
      [field]: value,
    })
  }

  const { t } = i18n
  const genderOptions = [
    { value: '', select: t('hospitality_room_reservation.customer_form.gender_placeholder') },
    { value: 'masculino', select: t('hospitality_room_reservation.customer_form.gender_male') },
    { value: 'feminino', select: t('hospitality_room_reservation.customer_form.gender_female') },
    { value: 'outro', select: t('hospitality_room_reservation.customer_form.gender_other') },
  ]

  const phoneTypeOptions = [
    { value: 'cell', select: t('hospitality_room_reservation.customer_form.phone_type_cell') },
    { value: 'landline', select: t('hospitality_room_reservation.customer_form.phone_type_landline') },
  ]

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '')
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }

  const formatCEP = (value: string) => {
    const numbers = value.replace(/\D/g, '')
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2')
  }

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  }

  // Função para buscar CEP
  const handleCEPChange = async (cep: string) => {
    const cleanCEP = cep.replace(/\D/g, '')
    handleInputChange('zipCode', formatCEP(cep))

    if (cleanCEP.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`)
        const data = await response.json()

        if (!data.erro) {
          onCustomerChange({
            ...customerData,
            zipCode: formatCEP(cep),
            street: data.logradouro || '',
            district: data.bairro || '',
            city: data.localidade || '',
            state: data.uf || '',
          })
        }
      } catch (error) {
        console.log('Erro ao buscar CEP:', error)
      }
    }
  }

  // Função para buscar Usuario por CPF
  const handleCPFChange = async (cpf: string) => {
    const cleanCPF = cpf.replace(/\D/g, '')
    handleInputChange('cpf', formatCPF(cpf))

    if (cleanCPF.length === 11) {
      try {
        const response = await POST('customer/filter', {
          cpf: cleanCPF
        });

        if (response && response.data[0] && response.data[0].id) {
          // Cliente encontrado, preencher o formulário com os dados
          const customer = response.data[0];
          // Extrair dados do endereço principal
          const primaryAddress = customer.customerAddresses && customer.customerAddresses.length > 0
            ? customer.customerAddresses[0]
            : {};

          // Extrair dados do telefone principal
          const primaryPhone = customer.customerPhones && customer.customerPhones.length > 0
            ? customer.customerPhones[0]
            : {};

          // Formatar telefone se existir
          const formattedPhone = primaryPhone.numberPhone ? formatPhone(primaryPhone.numberPhone) : "";
          onCustomerChange({
            ...customerData,
            cpf: formatCPF(customer.cpf || ""),
            name: customer.name || "",
            email: customer.email || "",
            birthDate: customer.birthDate ? customer.birthDate.split('T')[0] : "",
            gender: customer.gender || "",
            phoneNumber: formattedPhone,
            phoneType: primaryPhone.typePhone === "principal" ? "cell" : "cell",
            optinWhatsapp: customer.optinWhatsapp || false,
            zipCode: primaryAddress.zipCode ? formatCEP(primaryAddress.zipCode) : "",
            street: primaryAddress.street || "",
            number: primaryAddress.number || "",
            extra: primaryAddress.extra || "",
            district: primaryAddress.district || "",
            city: primaryAddress.city || "",
            state: primaryAddress.state || "",
            reference: primaryAddress.reference || "",
          });
        } else {
          onCustomerChange({
            ...customerData,
            cpf: formatCPF(cpf),
            name: "",
            email: "",
            birthDate: "",
            gender: "",
            phoneNumber: "",
            phoneType: "cell",
            optinWhatsapp: false,
            zipCode: "",
            street: "",
            number: "",
            extra: "",
            district: "",
            city: "",
            state: "",
            reference: "",
          })
        }
      } catch (error) {
        console.log('Erro ao buscar CEP:', error)
      }
    }
  }

  return (
    <div>
      {showTitle && (
        <h4 className='mb-4'>
          <i className='fas fa-user me-2'></i>
          {t('hospitality_room_reservation.customer_form.title')}
        </h4>
      )}

      {/* Dados Pessoais */}
      <div className='mb-6'>
        <h5 className='mb-4'>
          <i className='fas fa-id-card me-2'></i>
          {t('hospitality_room_reservation.customer_form.personal_info_title')}
        </h5>
        <div className='row'>
          <div className='col-md-6'>
            <Input
              label={t('hospitality_room_reservation.customer_form.cpf_label')}
              placeholder={t('hospitality_room_reservation.customer_form.cpf_placeholder')}
              value={customerData.cpf}
              change={(value: string) => handleCPFChange(value)}
              disabled={disabled}
              inputAttr={{ type: 'cpf', maxLength: 14 }}
            />
          </div>
          <div className='col-md-6'>
            <Input
              label={t('hospitality_room_reservation.customer_form.name_label')}
              placeholder={t('hospitality_room_reservation.customer_form.name_placeholder')}
              value={customerData.name}
              change={(value: string) => handleInputChange('name', value)}
              disabled={disabled}
              inputAttr={{ type: 'default' }}
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-md-4'>
            <Input
              label={t('hospitality_room_reservation.customer_form.email_label')}
              placeholder={t('hospitality_room_reservation.customer_form.email_placeholder')}
              value={customerData.email}
              change={(value: string) => handleInputChange('email', value)}
              disabled={disabled}
              inputAttr={{ type: 'email' }}
            />
          </div>
          <div className='col-md-4'>
            <Input
              label={t('hospitality_room_reservation.customer_form.birthdate_label')}
              value={customerData.birthDate}
              change={(value: string) => handleInputChange('birthDate', value)}
              disabled={disabled}
              inputAttr={{ type: 'date' }}
            />
          </div>
          <div className='col-md-4'>
            <Input
              label={t('hospitality_room_reservation.customer_form.gender_label')}
              value={customerData.gender}
              change={(value: string) => handleInputChange('gender', value as 'masculino' | 'feminino' | 'outro' | '')}
              disabled={disabled}
              options={genderOptions}
              inputAttr={{ type: 'select' }}
            />
          </div>
        </div>
      </div>

      {/* Contato */}
      <div className='mb-6'>
        <h5 className='mb-4'>
          <i className='fas fa-phone me-2'></i>
          {t('hospitality_room_reservation.customer_form.contact_info_title')}
        </h5>
        <div className='row'>
          <div className='col-md-4'>
            <Input
              label={t('hospitality_room_reservation.customer_form.phone_label')}
              placeholder={t('hospitality_room_reservation.customer_form.phone_placeholder')}
              value={customerData.phoneNumber}
              change={(value: string) => handleInputChange('phoneNumber', formatPhone(value))}
              disabled={disabled}
              inputAttr={{ type: 'tel', maxLength: 15 }}
            />
          </div>
          <div className='col-md-4'>
            <Input
              label={t('hospitality_room_reservation.customer_form.phone_type_label')}
              value={customerData.phoneType}
              change={(value: string) => handleInputChange('phoneType', value as 'cell' | 'landline')}
              disabled={disabled}
              options={phoneTypeOptions}
              inputAttr={{ type: 'select' }}
            />
          </div>
          <div className='col-md-4'>
            <div className='form-check form-switch mt-8'>
              <input
                className='form-check-input'
                type='checkbox'
                id='optinWhatsapp'
                checked={customerData.optinWhatsapp}
                onChange={(e) => handleInputChange('optinWhatsapp', e.target.checked)}
                disabled={disabled}
              />
              <label className='form-check-label' htmlFor='optinWhatsapp'>
                {t('hospitality_room_reservation.customer_form.whatsapp_optin')}
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Endereço */}
      <div className='mb-6'>
        <h5 className='mb-4'>
          <i className='fas fa-map-marker-alt me-2'></i>
          {t('hospitality_room_reservation.customer_form.address_title')}
        </h5>
        <div className='row'>
          <div className='col-md-3'>
            <Input
              label={t('hospitality_room_reservation.customer_form.cep_label')}
              placeholder={t('hospitality_room_reservation.customer_form.cep_placeholder')}
              value={customerData.zipCode}
              change={handleCEPChange}
              disabled={disabled}
              inputAttr={{ type: 'cep', maxLength: 9 }}
            />
          </div>
          <div className='col-md-6'>
            <Input
              label={t('hospitality_room_reservation.customer_form.street_label')}
              placeholder={t('hospitality_room_reservation.customer_form.street_placeholder')}
              value={customerData.street}
              change={(value: string) => handleInputChange('street', value)}
              disabled={disabled}
              inputAttr={{ type: 'default' }}
            />
          </div>
          <div className='col-md-3'>
            <Input
              label={t('hospitality_room_reservation.customer_form.number_label')}
              placeholder={t('hospitality_room_reservation.customer_form.number_placeholder')}
              value={customerData.number}
              change={(value: string) => handleInputChange('number', value)}
              disabled={disabled}
              inputAttr={{ type: 'default' }}
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-md-3'>
            <Input
              label={t('hospitality_room_reservation.customer_form.extra_label')}
              placeholder={t('hospitality_room_reservation.customer_form.extra_placeholder')}
              value={customerData.extra || ''}
              change={(value: string) => handleInputChange('extra', value)}
              disabled={disabled}
              inputAttr={{ type: 'default' }}
            />
          </div>
          <div className='col-md-3'>
            <Input
              label={t('hospitality_room_reservation.customer_form.district_label')}
              placeholder={t('hospitality_room_reservation.customer_form.district_placeholder')}
              value={customerData.district}
              change={(value: string) => handleInputChange('district', value)}
              disabled={disabled}
              inputAttr={{ type: 'default' }}
            />
          </div>
          <div className='col-md-4'>
            <Input
              label={t('hospitality_room_reservation.customer_form.city_label')}
              placeholder={t('hospitality_room_reservation.customer_form.city_placeholder')}
              value={customerData.city}
              change={(value: string) => handleInputChange('city', value)}
              disabled={disabled}
              inputAttr={{ type: 'default' }}
            />
          </div>
          <div className='col-md-2'>
            <Input
              label={t('hospitality_room_reservation.customer_form.state_label')}
              placeholder={t('hospitality_room_reservation.customer_form.state_placeholder')}
              value={customerData.state}
              change={(value: string) => handleInputChange('state', value.toUpperCase())}
              disabled={disabled}
              inputAttr={{ type: 'default', maxLength: 2 }}
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-md-12'>
            <Input
              label={t('hospitality_room_reservation.customer_form.reference_label')}
              placeholder={t('hospitality_room_reservation.customer_form.reference_placeholder')}
              value={customerData.reference || ''}
              change={(value: string) => handleInputChange('reference', value)}
              disabled={disabled}
              inputAttr={{ type: 'default' }}
            />
          </div>
        </div>
      </div>

      {/* Termos e Condições */}
      <div className='mb-6'>
        <h5 className='mb-4'>
          <i className='fas fa-file-contract me-2'></i>
          {t('hospitality_room_reservation.customer_form.terms_title')}
        </h5>
        <div className='row'>
          <div className='col-md-6'>
            <div className='form-check mb-3'>
              <input
                className='form-check-input'
                type='checkbox'
                id='acceptTerms'
                checked={customerData.acceptTerms}
                onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                disabled={disabled}
                required
              />
              <label className='form-check-label' htmlFor='acceptTerms'>
                <strong>{t('hospitality_room_reservation.customer_form.accept_terms')}</strong>
              </label>
            </div>
          </div>
          <div className='col-md-6'>
            <div className='form-check mb-3'>
              <input
                className='form-check-input'
                type='checkbox'
                id='lgpdConsent'
                checked={customerData.lgpdConsent}
                onChange={(e) => handleInputChange('lgpdConsent', e.target.checked)}
                disabled={disabled}
                required
              />
              <label className='form-check-label' htmlFor='lgpdConsent'>
                <strong>{t('hospitality_room_reservation.customer_form.lgpd_consent')}</strong>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Função para validar o formulário de cliente completo
export const validateCustomerForm = (customerData: CustomerFormData): { isValid: boolean; message?: string } => {
  // Validar nome completo
  const nameParts = customerData.name.trim().split(' ')
  if (!customerData.name.trim() || nameParts.length < 2) {
    return { isValid: false, message: 'Nome e sobrenome são obrigatórios!' }
  }

  // Validar email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!customerData.email.trim() || !emailRegex.test(customerData.email)) {
    return { isValid: false, message: 'E-mail válido é obrigatório!' }
  }

  // Validar CPF
  const cpfNumbers = customerData.cpf.replace(/\D/g, '')
  if (!cpfNumbers || cpfNumbers.length !== 11) {
    return { isValid: false, message: 'CPF deve conter 11 dígitos válidos!' }
  }

  // Validar data de nascimento
  if (!customerData.birthDate) {
    return { isValid: false, message: 'Data de nascimento é obrigatória!' }
  }
  const birthDate = new Date(customerData.birthDate)
  const now = new Date()
  if (isNaN(birthDate.getTime()) || birthDate > now) {
    return { isValid: false, message: 'Data de nascimento inválida!' }
  }

  // Validar telefone
  const phoneNumbers = customerData.phoneNumber.replace(/\D/g, '')
  if (!phoneNumbers || phoneNumbers.length < 10) {
    return { isValid: false, message: 'Telefone deve conter pelo menos 10 dígitos válidos!' }
  }

  // Validar endereço
  if (!customerData.zipCode.trim()) {
    return { isValid: false, message: 'CEP é obrigatório!' }
  }
  if (!customerData.street.trim()) {
    return { isValid: false, message: 'Logradouro é obrigatório!' }
  }
  if (!customerData.number.trim()) {
    return { isValid: false, message: 'Número é obrigatório!' }
  }
  if (!customerData.district.trim()) {
    return { isValid: false, message: 'Bairro é obrigatório!' }
  }
  if (!customerData.city.trim()) {
    return { isValid: false, message: 'Cidade é obrigatória!' }
  }
  if (!customerData.state.trim() || customerData.state.length !== 2) {
    return { isValid: false, message: 'Estado deve conter 2 caracteres!' }
  }

  // Validar termos
  if (!customerData.acceptTerms) {
    return { isValid: false, message: 'Você deve aceitar os termos e condições!' }
  }
  if (!customerData.lgpdConsent) {
    return { isValid: false, message: 'Você deve autorizar o tratamento dos dados pessoais!' }
  }

  return { isValid: true }
}

// Função para formatar os dados do cliente para envio à API
export const formatCustomerDataForAPI = (customerData: CustomerFormData): CustomerRegistrationData => {
  const cleanCPF = customerData.cpf.replace(/\D/g, '')
  const cleanPhone = customerData.phoneNumber.replace(/\D/g, '')
  const cleanCEP = customerData.zipCode.replace(/\D/g, '')

  return {
    name: customerData.name.trim(),
    email: customerData.email.trim().toLowerCase(),
    doc: cleanCPF,
    cpf: cleanCPF,
    birthDate: customerData.birthDate,
    gender: customerData.gender,
    optinWhatsapp: customerData.optinWhatsapp,
    lgpdDataAceiteCarta: customerData.lgpdConsent ? new Date() : undefined,
    phones: [
      {
        typePhone: customerData.phoneType === 'cell' ? 'cell' : 'landline',
        numberPhone: cleanPhone,
      },
    ],
    addresses: [
      {
        zipCode: cleanCEP,
        street: customerData.street.trim(),
        number: customerData.number.trim(),
        extra: customerData.extra?.trim() || '',
        district: customerData.district.trim(),
        city: customerData.city.trim(),
        state: customerData.state.trim().toUpperCase(),
        reference: customerData.reference?.trim() || '',
        typeAddress: 'main' as const,
      },
    ],
    validateRegister: true,
    status: 'active' as const,
  }
}

// Função para criar dados iniciais vazios do formulário
export const createEmptyCustomerData = (): CustomerFormData => ({
  name: '',
  email: '',
  cpf: '',
  birthDate: '',
  gender: '',
  phoneNumber: '',
  phoneType: 'cell',
  optinWhatsapp: false,
  zipCode: '',
  street: '',
  number: '',
  extra: '',
  district: '',
  city: '',
  state: '',
  reference: '',
  acceptTerms: false,
  lgpdConsent: false,
})

export { CustomerForm }
